import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity, Heart, Calendar, Pill, MapPin, Award, Users, BookOpen,
  Clock, Bell, ChevronRight, Check, Target, TrendingUp
} from 'lucide-react';
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout';

// ================================================================================
// STAT CARD COMPONENT
// ================================================================================
interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  trend?: string;
  color?: 'blue' | 'green' | 'amber';
}

const StatCard = React.memo(({ icon: Icon, label, value, trend, color = 'blue' }: StatCardProps) => {
  const colorStyles = useMemo(() => ({
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
  }), []);

  return (
    <GovCard className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className="text-xs text-green-600 flex items-center gap-0.5">
            <TrendingUp className="w-3 h-3" />
            {trend}
          </span>
        )}
      </div>
      <div className="mt-3">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </GovCard>
  );
});

StatCard.displayName = 'StatCard';

// ================================================================================
// PROGRESS TIMELINE COMPONENT
// ================================================================================
interface TimelineStep {
  status: 'completed' | 'current' | 'pending';
  title: string;
  description: string;
  date?: string;
}

const ProgressTimeline = React.memo(({ steps }: { steps: TimelineStep[] }) => {
  return (
    <div className="relative">
      {steps.map((step, index) => (
        <div key={index} className="relative pl-8 pb-6 last:pb-0">
          {index < steps.length - 1 && (
            <div className={`absolute left-3 top-8 bottom-0 w-0.5 ${
              step.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
            }`} />
          )}
          <div className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center ${
            step.status === 'completed' ? 'bg-green-500' :
            step.status === 'current' ? 'bg-blue-600 ring-4 ring-blue-100' :
            'bg-gray-200'
          }`}>
            {step.status === 'completed' && <Check className="w-4 h-4 text-white" />}
            {step.status === 'current' && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
          <div>
            <h4 className={`font-medium ${step.status === 'pending' ? 'text-gray-400' : 'text-gray-900'}`}>
              {step.title}
            </h4>
            <p className="text-sm text-gray-500">{step.description}</p>
            {step.date && <p className="text-xs text-gray-400 mt-0.5">{step.date}</p>}
          </div>
        </div>
      ))}
    </div>
  );
});

ProgressTimeline.displayName = 'ProgressTimeline';

// ================================================================================
// MEDICATION CARD COMPONENT
// ================================================================================
interface MedicationProps {
  name: string;
  dosage: string;
  time: string;
  taken: boolean;
}

const MedicationCard = React.memo(({ name, dosage, time, taken }: MedicationProps) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg border ${taken ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
      taken ? 'bg-green-100' : 'bg-amber-100'
    }`}>
      <Pill className={`w-5 h-5 ${taken ? 'text-green-600' : 'text-amber-600'}`} />
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="font-medium text-gray-900 truncate">{name}</h4>
      <p className="text-sm text-gray-500">{dosage} • {time}</p>
    </div>
    <GovButton variant={taken ? 'ghost' : 'primary'} size="sm">
      {taken ? 'Taken ✓' : 'Take Now'}
    </GovButton>
  </div>
));

MedicationCard.displayName = 'MedicationCard';

// ================================================================================
// HEALTH TIP COMPONENT
// ================================================================================
interface HealthTipProps {
  icon: React.ElementType;
  title: string;
  description: string;
  color: 'green' | 'blue' | 'cyan';
}

const HealthTip = React.memo(({ icon: Icon, title, description, color }: HealthTipProps) => {
  const colorStyles = useMemo(() => ({
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    cyan: 'bg-cyan-50 text-cyan-600',
  }), []);

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${colorStyles[color]}`}>
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm opacity-80">{description}</p>
      </div>
    </div>
  );
});

HealthTip.displayName = 'HealthTip';

// ================================================================================
// ACHIEVEMENT COMPONENT
// ================================================================================
interface AchievementProps {
  icon: React.ElementType;
  title: string;
  description: string;
  unlocked: boolean;
}

const AchievementBadge = React.memo(({ icon: Icon, title, description, unlocked }: AchievementProps) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg ${
    unlocked ? 'bg-amber-50' : 'bg-gray-50 opacity-60'
  }`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
      unlocked ? 'bg-amber-500' : 'bg-gray-200'
    }`}>
      <Icon className={`w-5 h-5 ${unlocked ? 'text-white' : 'text-gray-400'}`} />
    </div>
    <div className="flex-1 min-w-0">
      <h4 className={`font-medium truncate ${unlocked ? 'text-gray-900' : 'text-gray-400'}`}>
        {title}
      </h4>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
    {unlocked && <Check className="w-5 h-5 text-green-500" />}
  </div>
));

AchievementBadge.displayName = 'AchievementBadge';

// ================================================================================
// NEARBY CENTER COMPONENT
// ================================================================================
interface CenterProps {
  name: string;
  distance: string;
  type: string;
  rating: number;
}

const NearbyCenter = React.memo(({ name, distance, type, rating }: CenterProps) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
        <MapPin className="w-4 h-4 text-blue-600" />
      </div>
      <div>
        <h4 className="font-medium text-gray-900">{name}</h4>
        <p className="text-sm text-gray-500">{type} • {distance}</p>
      </div>
    </div>
    <div className="flex items-center gap-0.5 text-sm">
      <span className="text-amber-500">★</span>
      <span className="font-medium">{rating}</span>
    </div>
  </div>
));

NearbyCenter.displayName = 'NearbyCenter';

// ================================================================================
// MAIN CITIZEN DASHBOARD COMPONENT
// ================================================================================
const CitizenDashboard = () => {
  const { t } = useTranslation();

  // TB-related stats data
  const stats = useMemo(() => [
    { icon: Activity, label: t('dashboard.citizen.healthStatus'), value: 'Good', trend: '+5%', color: 'green' as const },
    { icon: Heart, label: t('dashboard.citizen.tbStatus'), value: 'Negative', color: 'blue' as const },
    { icon: Calendar, label: t('dashboard.citizen.nextAppointment'), value: '15 Jan', color: 'amber' as const },
    { icon: Award, label: t('dashboard.citizen.awarenessScore'), value: '850', trend: '+50', color: 'blue' as const },
  ], [t]);

  // Treatment progress steps
  const treatmentSteps = useMemo((): TimelineStep[] => [
    { status: 'completed', title: 'Initial Screening', description: 'TB test completed', date: '1 Dec 2025' },
    { status: 'completed', title: 'Diagnosis', description: 'Results: Negative', date: '3 Dec 2025' },
    { status: 'current', title: 'Follow-up', description: 'Regular check-up scheduled', date: '15 Jan 2026' },
    { status: 'pending', title: 'Final Assessment', description: 'Complete health review' },
  ], []);

  // Medication reminders
  const medications = useMemo((): MedicationProps[] => [
    { name: 'Vitamin D3', dosage: '1000 IU', time: 'Morning', taken: true },
    { name: 'Multivitamin', dosage: '1 tablet', time: 'After lunch', taken: false },
    { name: 'Iron Supplement', dosage: '65mg', time: 'Evening', taken: false },
  ], []);

  // Nearby health centers
  const nearbyCenters = useMemo((): CenterProps[] => [
    { name: 'District TB Center', distance: '2.5 km', type: 'Government Hospital', rating: 4.5 },
    { name: 'Community Health Center', distance: '3.2 km', type: 'Primary Healthcare', rating: 4.2 },
    { name: 'Private Clinic', distance: '4.0 km', type: 'Private', rating: 4.8 },
  ], []);

  // Achievements
  const achievements = useMemo((): AchievementProps[] => [
    { icon: BookOpen, title: 'Knowledge Seeker', description: 'Completed 10 articles', unlocked: true },
    { icon: Target, title: 'Health Tracker', description: 'Logged health for 30 days', unlocked: true },
    { icon: Users, title: 'Community Helper', description: 'Referred 5 members', unlocked: false },
  ], []);

  // Health tips
  const healthTips = useMemo((): HealthTipProps[] => [
    { icon: Heart, title: 'Stay Hydrated', description: 'Drink at least 8 glasses of water daily', color: 'green' },
    { icon: Activity, title: 'Regular Exercise', description: '30 minutes of daily activity recommended', color: 'blue' },
    { icon: Clock, title: 'Regular Check-ups', description: 'Schedule monthly health screenings', color: 'cyan' },
  ], []);

  return (
    <GovLayout>
      {/* Page Header */}
      <GovPageTitle
        title={`${t('dashboard.citizen.welcome')}, User!`}
        subtitle={`${t('dashboard.citizen.healthStatus')}: Healthy`}
        icon={Activity}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left Column - 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Treatment Progress */}
          <GovCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">{t('dashboard.citizen.treatmentProgress')}</h3>
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                Active
              </span>
            </div>
            <ProgressTimeline steps={treatmentSteps} />
          </GovCard>

          {/* Medication Reminders */}
          <GovCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">{t('dashboard.citizen.medicationReminders')}</h3>
              <Link to="/medications" className="text-sm text-blue-600 flex items-center gap-0.5 hover:underline">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-2">
              {medications.map((med, index) => (
                <MedicationCard key={index} {...med} />
              ))}
            </div>
          </GovCard>

          {/* Nearby Health Centers */}
          <GovCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">{t('dashboard.citizen.nearbyCenters')}</h3>
              <Link to="/centers" className="text-sm text-blue-600 flex items-center gap-0.5 hover:underline">
                View Map <MapPin className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-2">
              {nearbyCenters.map((center, index) => (
                <NearbyCenter key={index} {...center} />
              ))}
            </div>
          </GovCard>
        </div>

        {/* Right Column - 1 col */}
        <div className="space-y-4">
          {/* Achievements */}
          <GovCard>
            <h3 className="font-bold text-gray-900 mb-4">{t('dashboard.citizen.achievements')}</h3>
            <div className="space-y-2">
              {achievements.map((achievement, index) => (
                <AchievementBadge key={index} {...achievement} />
              ))}
            </div>
            <Link to="/achievements" className="block mt-4">
              <GovButton variant="secondary" className="w-full">
                View All Achievements
              </GovButton>
            </Link>
          </GovCard>

          {/* Health Tips */}
          <GovCard>
            <h3 className="font-bold text-gray-900 mb-4">{t('dashboard.citizen.healthTips')}</h3>
            <div className="space-y-2">
              {healthTips.map((tip, index) => (
                <HealthTip key={index} {...tip} />
              ))}
            </div>
          </GovCard>

          {/* Community Support */}
          <GovCard>
            <h3 className="font-bold text-gray-900 mb-4">{t('dashboard.citizen.communitySupport')}</h3>
            <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <p className="text-gray-500 text-sm mb-3">Join support groups and connect with others</p>
              <Link to="/groups">
                <GovButton variant="primary">Browse Groups</GovButton>
              </Link>
            </div>
          </GovCard>
        </div>
      </div>
    </GovLayout>
  );
};

export default React.memo(CitizenDashboard);